import { z } from 'zod';

import { McpAuthMode, McpConnectionStatus } from '../enums';

/**
 * Shared shape for all admin-configurable tool definitions (MCP, HTTP, skills,
 * client-side). Each source's contract extends this. Selection is a single
 * axis: a tool is exposed to a chat's LLM when it is assigned (org-wide via
 * `availableToAllChats`, or per-chat via `chatIds`) and `isActive`.
 */
export const baseToolDefinitionSchema = z.object({
  name: z.string().min(1).max(100),
  description: z.string().max(500).optional(),
  isActive: z.boolean().default(true),
  availableToAllChats: z.boolean().default(false),
  chatIds: z.array(z.string().uuid()).optional(),
});

const mcpIntegrationBaseSchema = baseToolDefinitionSchema.extend({
  serverUrl: z
    .string()
    .url()
    .refine(raw => ['http:', 'https:'].includes(new URL(raw).protocol), {
      message: 'MCP server URL must use HTTP or HTTPS',
    })
    .refine(raw => !new URL(raw).username && !new URL(raw).password, {
      message: 'MCP server URL must not contain credentials',
    }),
  authMode: z.nativeEnum(McpAuthMode).default(McpAuthMode.StaticBearer),
  /** Optional allowlist of discovered tool names to expose. */
  enabledTools: z.array(z.string()).optional(),
  /** Connector preset id (Phase 3C), if created from the catalogue. */
  connectorId: z.string().optional(),
});

const mcpDiscoveredToolSchema = z.object({
  name: z.string(),
  description: z.string().nullable().optional(),
});

export const mcpHeadersSchema = z.record(z.string());

/** Normalized MCP catalog record persisted by an explicit admin sync. */
const mcpObjectJsonSchema = z
  .object({
    type: z.literal('object'),
    properties: z.record(z.unknown()).optional(),
    required: z.array(z.string()).optional(),
  })
  .passthrough();

export const mcpToolDefinitionSchema = z.object({
  name: z.string().min(1).max(128),
  title: z.string().max(200).optional(),
  description: z.string().max(4000).optional(),
  inputSchema: mcpObjectJsonSchema,
  outputSchema: mcpObjectJsonSchema.optional(),
  annotations: z
    .object({
      title: z.string().max(200).optional(),
      readOnlyHint: z.boolean().optional(),
      destructiveHint: z.boolean().optional(),
      idempotentHint: z.boolean().optional(),
      openWorldHint: z.boolean().optional(),
    })
    .passthrough()
    .optional(),
  execution: z
    .object({
      taskSupport: z.enum(['forbidden', 'optional', 'required']).optional(),
    })
    .passthrough()
    .optional(),
});

/**
 * Create/update input. Accepts static-bearer credentials but never raw OAuth
 * tokens — OAuth is established through the dedicated connect flow.
 */
export const createMcpIntegrationSchema = mcpIntegrationBaseSchema.extend({
  apiKey: z.string().optional(),
  headers: mcpHeadersSchema.optional(),
});

export const updateMcpIntegrationSchema = createMcpIntegrationSchema
  .partial()
  .extend({ id: z.string().uuid() });

/**
 * API output. Secrets are masked (`hasApiKey` only), connection state and OAuth
 * metadata are exposed for the admin UI.
 */
export const mcpIntegrationOutputSchema = mcpIntegrationBaseSchema.extend({
  id: z.string().uuid(),
  connectionStatus: z.nativeEnum(McpConnectionStatus),
  hasApiKey: z.boolean(),
  connectedAt: z.date().optional(),
  tokenExpiresAt: z.date().optional(),
  oauthScopes: z.array(z.string()).optional(),
  lastAuthError: z.string().optional(),
  lastSyncedAt: z.date().optional(),
  catalogVersion: z.string().optional(),
  catalogSyncError: z.string().optional(),
  toolCount: z.number().optional(),
  /** Discovered tools (names + descriptions) from the last sync, for the allowlist UI. */
  discoveredTools: z.array(mcpDiscoveredToolSchema).optional(),
});

export type BaseToolDefinition = z.infer<typeof baseToolDefinitionSchema>;
export type CreateMcpIntegrationDTO = z.infer<
  typeof createMcpIntegrationSchema
>;
export type UpdateMcpIntegrationDTO = z.infer<
  typeof updateMcpIntegrationSchema
>;
export type McpIntegrationOutput = z.infer<typeof mcpIntegrationOutputSchema>;
export type McpToolDefinition = z.infer<typeof mcpToolDefinitionSchema>;
